import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Copy, Check, Search, Code, Palette, Layout, Type, Box } from 'lucide-react';
import { cn } from '../../lib/utils';

// CSS class reference for public docs page
const CSS_CLASSES = [
  // Layout & Structure
  {
    category: 'Layout & Structure',
    icon: Layout,
    classes: [
      {
        selector: '[data-testid="public-docs-page"]',
        description: 'Main container for the entire documentation page',
        example: `[data-testid="public-docs-page"] {\n  /* Full page styles */\n}`,
      },
      {
        selector: '[data-testid="public-docs-header"]',
        description: 'Top header/navigation bar',
        example: `[data-testid="public-docs-header"] {\n  background: #1a1a2e;\n  border-bottom: none;\n}`,
      },
      {
        selector: '[data-testid="public-docs-sidebar"]',
        description: 'Left sidebar containing document tree navigation',
        example: `[data-testid="public-docs-sidebar"] {\n  background: #f8f9fa;\n  width: 280px;\n}`,
      },
      {
        selector: '[data-testid="public-docs-main"]',
        description: 'Main content area',
        example: `[data-testid="public-docs-main"] {\n  max-width: 900px;\n  padding: 2rem;\n}`,
      },
      {
        selector: '[data-testid="public-docs-toc"]',
        description: 'Table of contents panel (right side)',
        example: `[data-testid="public-docs-toc"] {\n  display: none; /* Hide ToC */\n}`,
      },
      {
        selector: '[data-testid="public-docs-footer"]',
        description: 'Footer section',
        example: `[data-testid="public-docs-footer"] {\n  background: #1a1a2e;\n  color: white;\n}`,
      },
    ],
  },
  // Branding & Identity
  {
    category: 'Branding & Identity',
    icon: Palette,
    classes: [
      {
        selector: '[data-testid="public-docs-logo"]',
        description: 'Logo container in header',
        example: `[data-testid="public-docs-logo"] img {\n  max-height: 40px;\n}`,
      },
      {
        selector: '[data-testid="public-docs-title"]',
        description: 'Site/space title next to logo',
        example: `[data-testid="public-docs-title"] {\n  font-size: 1.25rem;\n  font-weight: 700;\n}`,
      },
      {
        selector: '--docs-primary',
        description: 'CSS variable for primary accent color (buttons, links, highlights)',
        example: `:root {\n  --docs-primary: #e94560;\n}`,
      },
      {
        selector: '--docs-secondary',
        description: 'CSS variable for secondary color (borders, backgrounds)',
        example: `:root {\n  --docs-secondary: #16213e;\n}`,
      },
    ],
  },
  // Navigation
  {
    category: 'Navigation',
    icon: Box,
    classes: [
      {
        selector: '[data-testid="public-docs-nav"]',
        description: 'Navigation container in sidebar',
        example: `[data-testid="public-docs-nav"] {\n  padding: 1rem;\n}`,
      },
      {
        selector: '[data-testid^="doc-tree-item-"]',
        description: 'Individual document links in navigation tree',
        example: `[data-testid^="doc-tree-item-"] {\n  border-radius: 8px;\n  padding: 0.75rem 1rem;\n}`,
      },
      {
        selector: '[data-testid="public-docs-toc-nav"]',
        description: 'Table of contents navigation links',
        example: `[data-testid="public-docs-toc-nav"] a {\n  font-size: 0.8rem;\n}`,
      },
    ],
  },
  // Content & Typography
  {
    category: 'Content & Typography',
    icon: Type,
    classes: [
      {
        selector: '[data-testid="public-docs-content"]',
        description: 'Document content wrapper',
        example: `[data-testid="public-docs-content"] {\n  font-size: 1rem;\n  line-height: 1.8;\n}`,
      },
      {
        selector: '[data-testid="public-docs-doc-title"]',
        description: 'Document title (h1)',
        example: `[data-testid="public-docs-doc-title"] {\n  font-size: 2.5rem;\n  color: #1a1a2e;\n}`,
      },
      {
        selector: '[data-testid="public-docs-doc-body"]',
        description: 'Document body content (prose)',
        example: `[data-testid="public-docs-doc-body"] p {\n  margin-bottom: 1.5rem;\n}\n\n[data-testid="public-docs-doc-body"] h2 {\n  margin-top: 2rem;\n}`,
      },
      {
        selector: '[data-testid="public-docs-updated-at"]',
        description: 'Last updated timestamp',
        example: `[data-testid="public-docs-updated-at"] {\n  font-style: italic;\n  opacity: 0.7;\n}`,
      },
    ],
  },
  // Interactive Elements
  {
    category: 'Interactive Elements',
    icon: Code,
    classes: [
      {
        selector: '[data-testid="search-trigger-button"]',
        description: 'Search button in header',
        example: `[data-testid="search-trigger-button"] {\n  background: #f0f0f0;\n  border: none;\n}`,
      },
      {
        selector: '[data-testid="public-docs-theme-toggle"]',
        description: 'Dark/light mode toggle button',
        example: `[data-testid="public-docs-theme-toggle"] {\n  display: none; /* Hide toggle */\n}`,
      },
      {
        selector: '[data-testid="mobile-menu-toggle"]',
        description: 'Mobile sidebar toggle button',
        example: `[data-testid="mobile-menu-toggle"] {\n  color: white;\n}`,
      },
      {
        selector: '[data-testid="clear-highlights-btn"]',
        description: 'Button to clear search highlights',
        example: `[data-testid="clear-highlights-btn"] {\n  display: none;\n}`,
      },
    ],
  },
  // States & Empty
  {
    category: 'States & Empty',
    icon: Box,
    classes: [
      {
        selector: '[data-testid="public-docs-empty-state"]',
        description: 'Empty state when no document is selected',
        example: `[data-testid="public-docs-empty-state"] {\n  padding: 4rem;\n}`,
      },
      {
        selector: '.search-highlight',
        description: 'Highlighted search matches in content',
        example: `.search-highlight {\n  background: #ffeb3b !important;\n  color: black !important;\n}`,
      },
    ],
  },
];

export default function CSSHelperModal({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedSelector, setCopiedSelector] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState('Layout & Structure');

  const handleCopy = async (text, selector) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSelector(selector);
      setTimeout(() => setCopiedSelector(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const filteredClasses = CSS_CLASSES.map(category => ({
    ...category,
    classes: category.classes.filter(
      cls =>
        cls.selector.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cls.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.classes.length > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" data-testid="css-helper-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-purple-600" />
            CSS Class Reference
          </DialogTitle>
          <DialogDescription>
            Use these CSS selectors to customize the appearance of your public documentation. Copy the examples and modify them in the Custom CSS editor.
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-4 flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search selectors or descriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="css-helper-search"
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6" data-testid="css-helper-scroll-area">
          <div className="space-y-4 pb-4">
            {filteredClasses.map((category) => {
              const Icon = category.icon;
              const isExpanded = expandedCategory === category.category;

              return (
                <div key={category.category} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
                    onClick={() => setExpandedCategory(isExpanded ? null : category.category)}
                  >
                    <Icon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <span className="font-medium text-slate-900 dark:text-white flex-1">
                      {category.category}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {category.classes.length} selectors
                    </Badge>
                  </button>

                  {isExpanded && (
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                      {category.classes.map((cls) => (
                        <div key={cls.selector} className="p-4 space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <code className="text-sm font-mono text-purple-600 dark:text-purple-400 break-all">
                                {cls.selector}
                              </code>
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                {cls.description}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-shrink-0"
                              onClick={() => handleCopy(cls.selector, cls.selector)}
                              data-testid={`copy-selector-${cls.selector.replace(/[^a-zA-Z0-9]/g, '-')}`}
                            >
                              {copiedSelector === cls.selector ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>

                          <div className="relative">
                            <pre className="bg-slate-900 dark:bg-slate-950 rounded-lg p-3 text-xs overflow-x-auto">
                              <code className="text-slate-100 font-mono">{cls.example}</code>
                            </pre>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="absolute top-2 right-2 h-7 px-2 text-slate-400 hover:text-white"
                              onClick={() => handleCopy(cls.example, `${cls.selector}-example`)}
                            >
                              {copiedSelector === `${cls.selector}-example` ? (
                                <>
                                  <Check className="w-3 h-3 mr-1 text-green-400" />
                                  <span className="text-xs">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3 mr-1" />
                                  <span className="text-xs">Copy</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {filteredClasses.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No selectors found matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            <strong>Tip:</strong> Use the browser's DevTools (F12) to inspect elements and find additional classes for fine-tuned customization.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
